import React, { useState } from 'react';
import { PRESET_AVATARS, AVATAR_CATEGORIES } from '../lib/avatars';

const AvatarSelector = ({ selected, onSelect, size = 'md' }) => {
  const [activeCategory, setActiveCategory] = useState('people');
  
  const sizeClasses = {
    sm: 'w-10 h-10 text-xl',
    md: 'w-12 h-12 text-2xl',
    lg: 'w-20 h-20 text-4xl',
  };

  const filteredAvatars = PRESET_AVATARS.filter(
    avatar => avatar.category === activeCategory
  );

  return (
    <div data-testid="avatar-selector" className="space-y-4">
      {/* Category Tabs */}
      <div className="flex gap-2 flex-wrap">
        {AVATAR_CATEGORIES.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => setActiveCategory(category.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeCategory === category.id
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            <span>{category.icon}</span>
            <span>{category.label}</span>
            <span className="text-xs opacity-70">
              ({PRESET_AVATARS.filter(a => a.category === category.id).length})
            </span>
          </button>
        ))}
      </div>

      {/* Avatar Grid */}
      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2 sm:gap-3" data-testid="avatar-grid">
        {filteredAvatars.map((avatar) => (
          <button
            key={avatar.id}
            type="button"
            onClick={() => onSelect(avatar.id)}
            data-testid={`avatar-${avatar.id}`}
            className={`${sizeClasses[size]} rounded-xl flex items-center justify-center transition-all duration-200 relative group ${
              selected === avatar.id
                ? 'ring-4 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900 scale-105 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30'
                : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 hover:scale-105'
            }`}
            title={avatar.label}
          >
            <span role="img" aria-label={avatar.label} className="text-2xl">
              {avatar.emoji}
            </span>
            {/* Tooltip on hover */}
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
              {avatar.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Display component for showing selected avatar
export const AvatarDisplay = ({ avatarId, size = 'md', className = '' }) => {
  const avatar = PRESET_AVATARS.find((a) => a.id === avatarId) || PRESET_AVATARS[0];
  
  const sizeClasses = {
    xs: 'w-8 h-8 text-lg',
    sm: 'w-10 h-10 text-xl',
    md: 'w-14 h-14 text-2xl',
    lg: 'w-20 h-20 text-4xl',
    xl: 'w-24 h-24 text-5xl',
  };

  return (
    <div 
      className={`${sizeClasses[size]} rounded-xl flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 ${className}`}
      style={{ 
        background: `linear-gradient(135deg, ${avatar.color}20, ${avatar.color}40)` 
      }}
    >
      <span role="img" aria-label={avatar.label}>
        {avatar.emoji}
      </span>
    </div>
  );
};

export default AvatarSelector;
